import { Limits } from '@studio/models/control/preset';
import type { Preset } from '@studio/models/control/preset';
import { forwardRef } from 'react';
import { Sel, STagNew } from '#~/stag/index.js';
import type { Props } from './types.js';
import { Editable, Settings, Thumbnail } from '#~/common/card/index.js';
import type { Behavior } from '#~/common/card/index.js';

/** Position preset `<Card>` component. */
export const Card = forwardRef<Behavior, Props>(({ handler, preset, ...props }, forwardedRef) => {
  const { thumbnail, ...item } = preset;

  return (
    <Editable<Preset>
      // Per rule: playwright-stag-selectors-only (ai.knowledge/platform) —
      // STag is the only selector the hardware preset-recall spec may use, and
      // it needs a per-record one: 27 ordered recalls cannot be addressed by
      // card position or by user-editable names.
      {...STagNew(Sel.MainRoomBottomTrayPresets, 'preset', item.id)}
      forwardedRef={forwardedRef}
      handler={{
        ...handler,
        onSettings: (id: Preset['id']): void => {
          handler.onRemove(id);
        }
      }}
      item={item}
      maxLength={Limits.maxNameLength}
      settings={
        // Element form so the remove control carries its own per-record STag:
        // the hardware spec has to remove exactly the presets it created, and a
        // bare icon gives it nothing stable to address.
        //
        // Passing an element REPLACES the default control that <Editable>
        // would have built, so this must reproduce that control completely —
        // its width and, critically, its onClick. An element without onClick
        // renders a trash button that does nothing for everyone, not just for
        // a test.
        <Settings
          className='w-18'
          icon='trash'
          onClick={(): void => {
            handler.onRemove(item.id);
          }}
          {...STagNew(Sel.MainRoomBottomTrayPresets, 'preset-remove', item.id)}
        />
      }
      {...props}
    >
      <Thumbnail image={thumbnail} title={item.name} />
    </Editable>
  );
});
